<template>
  <div 
    class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-5"
    :class="customClass">
    <!-- Map first on mobile when requested -->
    <div
      v-if="mobileMapFirst && mapPosition !== MAP_POSITION.MIDDLE"
      class="md:tw-hidden"
      :class="mapClass"
    >
      <client-only>
        <div
          :class="['fast-embeded tw-overflow-hidden', { 'tw-rounded-lg': mapRounded }]"
          v-html="mapEmbed"
        />
      </client-only>
    </div>

    <div
      class="tw-flex tw-flex-col"
      :class="contentClass"
    >
      <div
        class="tw-mb-3 tw-break-words"
        :class="branchNameClass"
      >
        {{ props.branchName || '-' }}
      </div>

      <!-- Map between branchName and contact items -->
      <div
        v-if="mapPosition === MAP_POSITION.MIDDLE"
        class="tw-py-3"
        :class="mapClass"
      >
        <client-only>
          <div
            class="fast-embeded fast-embeded--full tw-overflow-hidden"
            :class="[mapHeight, { 'tw-rounded-md': mapRounded }]"
            v-html="mapEmbed"
          />
        </client-only>
      </div>

      <!-- Loop through contact items -->
      <div class="tw-flex tw-flex-col tw-gap-2">
        <div
          v-for="item in visibleContactItems"
          :key="item.type"
          class="tw-flex tw-text-mb-body-small lg:tw-text-dt-body-small tw-text-dark-gray"
          :class="customClassContactItems"
        >
          <QIcon
            v-if="showIconContact"
            :name="item.iconClass"
            class="tw-mt-1"
          />
          <div v-if="label[item.type]" class="tw-whitespace-nowrap">
            {{ label[item.type] }}
          </div>
          <div
            :class="[{ 'tw-mt-1': showMarginAddress && item.type === CONTACT_TYPE.ADDRESS }, showIconContact && 'tw-ml-3']"
          >
            <!-- Regular text content for address -->
            <template v-if="item.type === CONTACT_TYPE.ADDRESS">
              {{ item.content }}
            </template>

            <!-- Multiple phone numbers -->
            <template v-else-if="item.type === CONTACT_TYPE.PHONE">
              <div
                v-for="(phoneNumber, index) in phoneNumbers"
                :key="index"
              >
                <ExternalLink
                  :href="`tel:${phoneNumber}`"
                  :title="$t('global.callNow')"
                  class="hover:tw-text-primary"
                  @click="eventContactUs($phoneNumberFormat(phoneNumber))"
                >
                  {{ $phoneNumberFormat(phoneNumber, ' ') }}
                </ExternalLink>
              </div>
            </template>

            <!-- Link items (line, email) -->
            <template v-else>
              <ExternalLink
                :href="item.href || ''"
                :title="item.title || ''"
                class="hover:tw-text-primary"
                @click="eventContactUs(item.eventValue || '')"
              >
                {{ item.content }}
              </ExternalLink>
            </template>
          </div>
        </div>
      </div>
    </div>

    <!-- Map at default position (beside content) -->
    <div
      v-if="mapPosition !== MAP_POSITION.MIDDLE"
      :class="[mobileMapFirst ? 'tw-hidden md:tw-block' : '', mapClass]"
    >
      <client-only>
        <div
          :class="['fast-embeded tw-overflow-hidden', { 'tw-rounded-lg': mapRounded }]"
          v-html="mapEmbed"
        />
      </client-only>
    </div>
  </div>
</template>

<script setup lang="ts">
const CONTACT_TYPE = {
  ADDRESS: 'address',
  PHONE: 'phone',
  LINE: 'line',
  EMAIL: 'email',
} as const

const MAP_POSITION = {
  DEFAULT: 'default',
  MIDDLE: 'middle',
} as const

const props = defineProps({
  branchName: {
    type: String,
    default: '',
  },
  branchNameClass: {
    type: String,
    default: 'tw-text-mb-title-3 lg:tw-text-dt-title-4 tw-text-primary',
  },
  address: {
    type: String,
    default: '',
  },
  phoneNumbers: {
    type: Array as () => string[],
    default: () => [],
  },
  lineId: {
    type: String,
    default: '',
  },
  email: {
    type: String,
    default: '',
  },
  mapEmbed: {
    type: String,
    default: '',
  },
  categoryEventName: {
    type: String,
    default: '',
  },
  showIconContact: {
    type: Boolean,
    default: true,
  },
  customClass: {
    type: String,
    default: '',
  },
  contentClass: {
    type: String,
    default: '',
  },
  mapClass: {
    type: String,
    default: '',
  },
  mapPosition: {
    type: String,
    default: 'default',
  },
  label: {
    type: Object as () => Record<string, string>,
    default: () => ({}),
  },
  customClassContactItems: {
    type: String,
    default: '',
  },
  showMarginAddress: {
    type: Boolean,
    default: true,
  },
  mapHeight: {
    type: String,
    default: 'tw-h-[100px]',
  },
  mapRounded: {
    type: Boolean,
    default: true,
  },
  mobileMapFirst: {
    type: Boolean,
    default: false,
  },
})

const { $phoneNumberFormat } = useNuxtApp()
const { getLineLink } = useSocialLink()
const { analyticEvent, fbPixelEvent } = useAnalytic()

// Type definition for contact item
interface ContactItem {
  type: string
  iconClass: string
  content: string
  href?: string
  title?: string
  eventValue?: string
  show: boolean
}

// Contact items computed property
const contactItems = computed<ContactItem[]>(() => [
  {
    type: CONTACT_TYPE.ADDRESS,
    iconClass: 'fas fa-location-dot',
    content: props.address,
    show: !!props.address,
  },
  {
    type: CONTACT_TYPE.PHONE,
    iconClass: 'fas fa-phone',
    content: '',
    show: props.phoneNumbers.length > 0,
  },
  {
    type: CONTACT_TYPE.LINE,
    iconClass: 'fa-brands fa-line',
    content: props.lineId || '',
    href: props.lineId ? getLineLink(props.lineId) : '',
    title: 'Line',
    eventValue: props.lineId ? getLineLink(props.lineId) : '',
    show: !!props.lineId,
  },
  {
    type: CONTACT_TYPE.EMAIL,
    iconClass: 'fas fa-envelope',
    content: props.email || '',
    href: props.email ? `mailto:${props.email}` : '',
    title: 'Email',
    eventValue: props.email ? `mailto:${props.email}` : '',
    show: !!props.email,
  },
])

// Filtered contact items - only show items with show=true
const visibleContactItems = computed(() => {
  return contactItems.value.filter(item => item.show)
})
const eventContactUs = (title: string) => {
  analyticEvent(
    {
      ga4ActionName: 'click_contact_cta',
      ga4Label: title,
    },
  )
  fbPixelEvent({
    fbEvent: 'Contact',
  })
}

</script>

<style>
.fast-embeded iframe {
  max-width: 100%;
  max-height: 14rem;
}

@media (min-width: 768px) {
  .fast-embeded iframe {
    max-width: 18rem;
    max-height: 12rem;
  }
}

.fast-embeded--full {
  position: relative;
}

.fast-embeded--full iframe {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 200%;
  height: 200%;
  max-width: none !important;
}

.address-content {
  font-size: var(--theme-font-text-mb-body-size);
  line-height: var(--theme-font-text-mb-body-height);
  font-weight: var(--theme-font-text-mb-body-weight);
}

@media (min-width: 1024px) {
  .address-content {
    font-size: var(--theme-font-text-dt-body-small-size);
    line-height: var(--theme-font-text-dt-body-small-height);
    font-weight: var(--theme-font-text-dt-body-small-weight);
  }
}
</style>
